'use client'
import { motion } from 'framer-motion'
import { useLang } from '../context/LangContext'
import { t, productDevOps } from '../lib/translations'
import SectionHeader from './SectionHeader'
import BenefitsBar from './BenefitsBar'
import FeatureCardGrid from './FeatureCardGrid'
import TrustedBy from './TrustedBy'
import LogosMarquee from './LogosMarquee'
import DevOpsToolsTabs from './DevOpsToolsTabs'
import Icon from './Icon'
const serviceIcons = ['adjustments', 'check-circle', 'refresh', 'chart-bar', 'shield', 'transfer', 'layers', 'code', 'apps'] as const
export default function DevOpsLanding() {
  const lang = useLang()
  const services = productDevOps.services.map((s, i) => ({
    title: t(lang, s.title),
    description: t(lang, s.desc),
    icon: <Icon name={serviceIcons[i]} className="w-6 h-6" strokeWidth={1.5} />,
  }))
  const stats = productDevOps.stats.map((s) => ({ title: t(lang, s.title), desc: t(lang, s.desc) }))

  return (
    <>
      {/* Hero */}
      <section id="devops" className="relative pt-32 pb-24 sm:pt-40 sm:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#4f46e5]/5 via-transparent to-transparent pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-[#6366f1]/15 rounded-full blur-[160px] pointer-events-none opacity-50" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-[#6366f1]/15 rounded-full blur-[140px] pointer-events-none opacity-75" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[300px] bg-[#6366f1]/10 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 inset-x-0 h-40 bg-gradient-to-t from-[#07070d] via-[#07070d]/80 to-transparent pointer-events-none z-10" />

        <div className="relative max-w-7xl mx-auto px-8 sm:px-8 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="text-sm font-medium text-[#818cf8] tracking-wide uppercase">{t(lang, productDevOps.heroKicker)}</span>
            <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight text-white">
              {t(lang, productDevOps.heroTitle1)}{' '}
              <span className="gradient-text">{t(lang, productDevOps.heroTitle2)}</span>
            </h1>
            <p className="mt-6 text-lg text-[#94a3b8] leading-relaxed max-w-2xl mx-auto">
              {t(lang, productDevOps.heroDesc)}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#cta"
                className="w-full sm:w-auto text-center px-6 py-3 rounded-lg bg-gradient-to-r from-[#4f46e5] to-[#7c3aed] text-white text-sm font-medium hover:from-[#6366f1] hover:to-[#c084fc] transition-all duration-200 shadow-lg shadow-[#4f46e5]/20 btn-lightning"
              >
                {t(lang, productDevOps.ctaStart)}
              </a>
              <a
                href="#devops-services"
                className="w-full sm:w-auto text-center px-6 py-3 rounded-lg border border-white/[0.1] text-[#94a3b8] text-sm font-medium transition-all duration-200 btn-outline"
              >
                {t(lang, productDevOps.ctaExplore)}
              </a>
            </div>

            <TrustedBy className="mt-16" />
          </motion.div>

          <LogosMarquee marginTop="mt-8" />
        </div>
      </section>

      <BenefitsBar
        items={stats}
        animated={false}
        gridClass="grid-cols-2 md:grid-cols-4 gap-8"
        padding="py-10"
      />

      {/* Services Grid */}
      <section id="devops-services" className="relative py-24 sm:py-32">
        <div className="absolute inset-0 dot-pattern pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-8 sm:px-8 lg:px-8">
          <SectionHeader
            heading={`${t(lang, productDevOps.featuresHeading)} `}
            highlighted={t(lang, productDevOps.featuresHighlight)}
            description={t(lang, productDevOps.featuresDesc)}
          />

          <FeatureCardGrid items={services} layout="inline" />
        </div>
      </section>

      {/* Workflow */}
      <section className="relative py-24 sm:py-32 bg-white/[0.005]">
        <div className="absolute inset-0 dot-pattern pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-8 sm:px-8 lg:px-8">
          <SectionHeader
            heading={`${t(lang, productDevOps.workflowTitle)} `}
            highlighted={t(lang, productDevOps.workflowHighlight)}
            description={t(lang, productDevOps.workflowDesc)}
          />

          <div className="relative mt-16 max-w-5xl mx-auto">
            {/* Horizontal Timeline Line */}
            <div className="hidden lg:block absolute top-6 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#4f46e5]/50 to-transparent z-0"></div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-6 relative z-10">
              {productDevOps.workflowSteps.map((w, i) => (
                <motion.div
                  key={t(lang, w.step)}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.5, delay: i * 0.12 }}
                  className="relative flex flex-col items-center text-center lg:items-start lg:text-start"
                >
                  <div className="text-6xl font-bold text-white/[0.03] absolute -top-4 right-0 lg:right-auto lg:left-8 select-none pointer-events-none">{t(lang, w.step)}</div>
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#4f46e5] to-[#7c3aed] flex items-center justify-center text-white font-bold text-sm mb-6 shadow-lg shadow-[#4f46e5]/20 ring-4 ring-[#07070d] relative z-10 mx-auto lg:mx-0">
                    {t(lang, w.step)}
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-3">{t(lang, w.title)}</h3>
                  <p className="text-sm text-[#94a3b8] leading-relaxed max-w-[260px] mx-auto lg:mx-0">{t(lang, w.desc)}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DevOps Tools Tabs */}
      <section className="relative py-24 sm:py-32">
        <div className="absolute inset-0 dot-pattern pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-8 sm:px-8 lg:px-8">
          <SectionHeader
            heading={`${t(lang, productDevOps.toolsHeading)}`}
            description={t(lang, productDevOps.toolsDesc)}
            highlighted=""
          />
          <DevOpsToolsTabs className="mt-12" />
        </div>
      </section>
    </>
  )
}
